import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

function getCurrentIsoWeekYear() {
  const now = new Date();
  const date = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const year = date.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const weekNumber = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { weekNumber, year };
}

function formatHours(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0,00";
  return num.toFixed(2).replace(".", ",");
}

function getBadgeClass(kind) {
  if (kind === "ok" || kind === "signed" || kind === "approved") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (kind === "pending" || kind === "fallback" || kind === "generated") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (kind === "missing" || kind === "rejected" || kind === "locked") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (kind === "invoiced") {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function StatCard({ label, value, hint = "", tone = "default" }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 ${getBadgeClass(tone)}`}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {hint ? <p className="mt-1 text-xs opacity-80">{hint}</p> : null}
    </div>
  );
}

function InfoBadge({ label, tone = "default" }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${getBadgeClass(tone)}`}>
      {label}
    </span>
  );
}

function PlaceholderCard({ title, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5">
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{text}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return <p className="text-sm text-slate-500">{text}</p>;
}

export default function UrenbeheerPage() {
  const defaults = getCurrentIsoWeekYear();
  const [weekNumber, setWeekNumber] = useState(String(defaults.weekNumber));
  const [year, setYear] = useState(String(defaults.year));
  const [includeExtendedPeriod, setIncludeExtendedPeriod] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [workorderId, setWorkorderId] = useState("");
  const [status, setStatus] = useState("all");
  const [lockStatus, setLockStatus] = useState("all");
  const [onlyExceptions, setOnlyExceptions] = useState(false);
  const [onlyWithoutMandagstaat, setOnlyWithoutMandagstaat] = useState(false);
  const [activeTab, setActiveTab] = useState("exceptions");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    meta: {
      weekNumber: defaults.weekNumber,
      year: defaults.year,
      monthLabel: "",
      includeExtendedPeriod: false,
    },
    summary: {
      totalHoursWeek: 0,
      totalHoursMonth: null,
      entryCount: 0,
      hoursWithoutMandagstaat: 0,
      hoursWithMandagstaat: 0,
      lockedHours: 0,
      exceptionCount: 0,
    },
    inbox: {
      active: false,
      count: 0,
      entries: [],
    },
    entries: [],
    exceptions: [],
    locks: [],
    mandagstaatSummary: {
      withoutMandagstaatHours: 0,
      generatedHours: 0,
      signedHours: 0,
      invoicedHours: 0,
      hoursPerWeek: [],
      hoursPerClient: [],
    },
    filterOptions: {
      employees: [],
      clients: [],
      projects: [],
      workorders: [],
      statuses: [],
      lockStatuses: [],
    },
  });

  useEffect(() => {
    let cancelled = false;

    async function loadOverview() {
      const week = Number(weekNumber);
      const selectedYear = Number(year);
      if (!Number.isInteger(week) || week < 1 || week > 53) return;
      if (!Number.isInteger(selectedYear) || selectedYear < 2000 || selectedYear > 2100) return;

      try {
        setLoading(true);
        setError("");
        const res = await api.get("/urenbeheer/overview", {
          params: {
            weekNumber: week,
            year: selectedYear,
            includeExtendedPeriod,
            employeeId: employeeId || undefined,
            clientId: clientId || undefined,
            projectId: projectId || undefined,
            workorderId: workorderId || undefined,
            status,
            lockStatus,
            onlyExceptions,
            onlyWithoutMandagstaat,
          },
        });

        if (!cancelled) {
          setData({
            meta: res?.data?.meta || {},
            summary: res?.data?.summary || {},
            inbox: res?.data?.inbox || { active: false, count: 0, entries: [] },
            entries: Array.isArray(res?.data?.entries) ? res.data.entries : [],
            exceptions: Array.isArray(res?.data?.exceptions) ? res.data.exceptions : [],
            locks: Array.isArray(res?.data?.locks) ? res.data.locks : [],
            mandagstaatSummary: res?.data?.mandagstaatSummary || {},
            filterOptions: res?.data?.filterOptions || {},
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || "Urenbeheer laden mislukt");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOverview();

    return () => {
      cancelled = true;
    };
  }, [
    weekNumber,
    year,
    includeExtendedPeriod,
    employeeId,
    clientId,
    projectId,
    workorderId,
    status,
    lockStatus,
    onlyExceptions,
    onlyWithoutMandagstaat,
  ]);

  const employeeOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.employees) ? data.filterOptions.employees : []),
    [data]
  );
  const clientOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.clients) ? data.filterOptions.clients : []),
    [data]
  );
  const projectOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.projects) ? data.filterOptions.projects : []),
    [data]
  );
  const workorderOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.workorders) ? data.filterOptions.workorders : []),
    [data]
  );
  const statusOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.statuses) ? data.filterOptions.statuses : []),
    [data]
  );
  const lockStatusOptions = useMemo(
    () => (Array.isArray(data?.filterOptions?.lockStatuses) ? data.filterOptions.lockStatuses : []),
    [data]
  );

  useEffect(() => {
    if (employeeId && !employeeOptions.some((item) => String(item._id) === String(employeeId))) {
      setEmployeeId("");
    }
  }, [employeeId, employeeOptions]);

  useEffect(() => {
    if (clientId && !clientOptions.some((item) => String(item._id) === String(clientId))) {
      setClientId("");
    }
  }, [clientId, clientOptions]);

  useEffect(() => {
    if (projectId && !projectOptions.some((item) => String(item._id) === String(projectId))) {
      setProjectId("");
    }
  }, [projectId, projectOptions]);

  useEffect(() => {
    if (workorderId && !workorderOptions.some((item) => String(item._id) === String(workorderId))) {
      setWorkorderId("");
    }
  }, [workorderId, workorderOptions]);

  const tabs = useMemo(
    () => [
      { key: "inbox", label: "Inbox" },
      { key: "exceptions", label: "Exceptions" },
      { key: "correcties", label: "Correcties" },
      { key: "locks", label: "Locks" },
      { key: "mandagstaat", label: "Mandagstaat" },
      { key: "export", label: "Export" },
    ],
    []
  );

  function clearFilters() {
    setIncludeExtendedPeriod(false);
    setEmployeeId("");
    setClientId("");
    setProjectId("");
    setWorkorderId("");
    setStatus("all");
    setLockStatus("all");
    setOnlyExceptions(false);
    setOnlyWithoutMandagstaat(false);
  }

  function renderInboxTab() {
    if (!Array.isArray(data?.inbox?.entries) || data.inbox.entries.length === 0) {
      return (
        <PlaceholderCard
          title="Inbox nog beperkt actief"
          text="Ingediende uren die review of goedkeuring nodig hebben worden hier zichtbaar zodra er entries met reviewstatus beschikbaar zijn."
        />
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-3">Datum</th>
              <th className="py-2 pr-3">Medewerker</th>
              <th className="py-2 pr-3">Werkbon</th>
              <th className="py-2 pr-3">Uren</th>
              <th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.inbox.entries.map((entry) => (
              <tr key={entry._id} className="border-b">
                <td className="py-2 pr-3">{entry.dateLabel}</td>
                <td className="py-2 pr-3">{entry.employeeName}</td>
                <td className="py-2 pr-3">{entry.workorderNumber || "-"}</td>
                <td className="py-2 pr-3">{formatHours(entry.hours)}</td>
                <td className="py-2 pr-3">
                  <InfoBadge label={entry.approvalStatusLabel} tone="pending" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderExceptionsTab() {
    if (!Array.isArray(data?.exceptions) || data.exceptions.length === 0) {
      return <EmptyState text="Geen uitzonderingen gevonden voor deze selectie." />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-3">Datum</th>
              <th className="py-2 pr-3">Medewerker</th>
              <th className="py-2 pr-3">Context</th>
              <th className="py-2 pr-3">Uren</th>
              <th className="py-2 pr-3">Issues</th>
            </tr>
          </thead>
          <tbody>
            {data.exceptions.map((item) => (
              <tr key={item.entryId} className="border-b align-top">
                <td className="py-2 pr-3">{item.dateLabel}</td>
                <td className="py-2 pr-3">
                  <div className="font-medium">{item.employeeName}</div>
                  <div className="text-xs text-slate-500">{item.personnelNumber || "Geen personeelsnr"}</div>
                </td>
                <td className="py-2 pr-3">
                  <div>{item.clientName || "-"}</div>
                  <div className="text-xs text-slate-500">
                    {item.projectName || item.workorderNumber || "Geen context"}
                  </div>
                </td>
                <td className="py-2 pr-3">{formatHours(item.hours)}</td>
                <td className="py-2 pr-3">
                  <div className="flex flex-wrap gap-2">
                    {(item.exceptionLabels || []).map((label) => (
                      <InfoBadge key={`${item.entryId}:${label}`} label={label} tone="missing" />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderLocksTab() {
    if (!Array.isArray(data?.locks) || data.locks.length === 0) {
      return <EmptyState text="Geen financieel gelockte uren in deze selectie." />;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-3">Medewerker</th>
              <th className="py-2 pr-3">Datum</th>
              <th className="py-2 pr-3">Uren</th>
              <th className="py-2 pr-3">Lockreden</th>
              <th className="py-2 pr-3">Bronreferentie</th>
              <th className="py-2 pr-3">Mandagstaat</th>
            </tr>
          </thead>
          <tbody>
            {data.locks.map((item) => (
              <tr key={item.entryId} className="border-b">
                <td className="py-2 pr-3">{item.employeeName}</td>
                <td className="py-2 pr-3">{item.dateLabel}</td>
                <td className="py-2 pr-3">{formatHours(item.hours)}</td>
                <td className="py-2 pr-3">{item.lockReasonLabel}</td>
                <td className="py-2 pr-3">{item.lockRefType || "-"}</td>
                <td className="py-2 pr-3">
                  {item.mandagstaatReference ? (
                    <div className="flex flex-wrap gap-2">
                      <span>{item.mandagstaatReference}</span>
                      <InfoBadge label={item.mandagstaatStatusLabel} tone={item.mandagstaatStatus} />
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderMandagstaatTab() {
    const summary = data?.mandagstaatSummary || {};
    const hoursPerWeek = Array.isArray(summary.hoursPerWeek) ? summary.hoursPerWeek : [];
    const hoursPerClient = Array.isArray(summary.hoursPerClient) ? summary.hoursPerClient : [];
    const extendedPeriodEnabled = summary.extendedPeriodEnabled === true;

    return (
      <div className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Zonder mandagstaat" value={`${formatHours(summary.withoutMandagstaatHours)} u`} />
          <StatCard label="In generated" value={`${formatHours(summary.generatedHours)} u`} tone="pending" />
          <StatCard label="In signed" value={`${formatHours(summary.signedHours)} u`} tone="signed" />
          <StatCard label="In invoiced" value={`${formatHours(summary.invoicedHours)} u`} tone="invoiced" />
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 p-4">
            <h3 className="text-base font-semibold text-slate-900">Uren per week</h3>
            <div className="mt-3 overflow-x-auto">
              {!extendedPeriodEnabled ? (
                <EmptyState text="Activeer 'Toon maand/ruimere periode' om weekrollup buiten de primaire weekselectie te laden." />
              ) : hoursPerWeek.length === 0 ? (
                <EmptyState text="Geen uren per week beschikbaar voor deze selectie." />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 pr-3">Week</th>
                      <th className="py-2 pr-3">Jaar</th>
                      <th className="py-2 pr-3">Uren</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursPerWeek.map((item) => (
                      <tr key={item.key} className="border-b">
                        <td className="py-2 pr-3">{item.weekNumber}</td>
                        <td className="py-2 pr-3">{item.year}</td>
                        <td className="py-2 pr-3">{formatHours(item.hours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 p-4">
            <h3 className="text-base font-semibold text-slate-900">Uren per klant</h3>
            <div className="mt-3 overflow-x-auto">
              {hoursPerClient.length === 0 ? (
                <EmptyState text="Geen klantverdeling beschikbaar voor deze selectie." />
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 pr-3">Klant</th>
                      <th className="py-2 pr-3">Regels</th>
                      <th className="py-2 pr-3">Uren</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoursPerClient.map((item) => (
                      <tr key={item.clientId || item.clientName} className="border-b">
                        <td className="py-2 pr-3">{item.clientName}</td>
                        <td className="py-2 pr-3">{item.entryCount}</td>
                        <td className="py-2 pr-3">{formatHours(item.hours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTabContent() {
    if (activeTab === "inbox") return renderInboxTab();
    if (activeTab === "exceptions") return renderExceptionsTab();
    if (activeTab === "correcties") {
      return (
        <PlaceholderCard
          title="Correcties"
          text="Correcties en herboekingen komen in een volgende fase."
        />
      );
    }
    if (activeTab === "locks") return renderLocksTab();
    if (activeTab === "mandagstaat") return renderMandagstaatTab();
    return (
      <PlaceholderCard
        title="Export"
        text="Week/maand export voor administratie volgt later."
      />
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs uppercase tracking-wide text-slate-500">Uren</p>
        <h1 className="text-2xl font-semibold text-slate-900">Urenbeheer</h1>
        <p className="mt-2 text-sm text-slate-600">
          Centrale controle op uren, uitzonderingen, mandagstaten en locks.
        </p>
      </section>

      <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
        <StatCard label="Totaal uren deze week" value={`${formatHours(data?.summary?.totalHoursWeek)} u`} />
        <StatCard
          label="Totaal uren deze maand"
          value={data?.summary?.totalHoursMonth === null ? "-" : `${formatHours(data?.summary?.totalHoursMonth)} u`}
          hint={
            data?.meta?.includeExtendedPeriod
              ? data?.meta?.monthLabel || ""
              : "Activeer 'Toon maand/ruimere periode'"
          }
        />
        <StatCard label="Aantal urenregels" value={String(data?.summary?.entryCount || 0)} />
        <StatCard label="Uren zonder mandagstaat" value={`${formatHours(data?.summary?.hoursWithoutMandagstaat)} u`} tone="missing" />
        <StatCard label="Uren gekoppeld" value={`${formatHours(data?.summary?.hoursWithMandagstaat)} u`} tone="pending" />
        <StatCard label="Gelockte uren" value={`${formatHours(data?.summary?.lockedHours)} u`} tone="locked" />
        <StatCard label="Uitzonderingen" value={String(data?.summary?.exceptionCount || 0)} tone="missing" />
      </section>

      <section className="rounded-2xl bg-white p-4 shadow space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Filters</h2>
          <p className="text-sm text-slate-500">
            Selecteer week, context en status om uren, uitzonderingen en mandagstaatkoppelingen te controleren.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Week</span>
            <input
              type="number"
              min="1"
              max="53"
              value={weekNumber}
              onChange={(event) => setWeekNumber(event.target.value)}
              className="w-full rounded border p-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Jaar</span>
            <input
              type="number"
              min="2000"
              max="2100"
              value={year}
              onChange={(event) => setYear(event.target.value)}
              className="w-full rounded border p-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Medewerker</span>
            <select
              value={employeeId}
              onChange={(event) => setEmployeeId(event.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle medewerkers</option>
              {employeeOptions.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Klant</span>
            <select
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle klanten</option>
              {clientOptions.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Project</span>
            <select
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle projecten</option>
              {projectOptions.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Werkbon</span>
            <select
              value={workorderId}
              onChange={(event) => setWorkorderId(event.target.value)}
              className="w-full rounded border p-2"
            >
              <option value="">Alle werkbonnen</option>
              {workorderOptions.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Status</span>
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="w-full rounded border p-2"
            >
              {(statusOptions.length > 0 ? statusOptions : [{ value: "all", label: "Alle statussen" }]).map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-slate-600">Lockstatus</span>
            <select
              value={lockStatus}
              onChange={(event) => setLockStatus(event.target.value)}
              className="w-full rounded border p-2"
            >
              {(lockStatusOptions.length > 0 ? lockStatusOptions : [{ value: "all", label: "Alle lockstatussen" }]).map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col justify-end gap-3">
            <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={includeExtendedPeriod}
                onChange={(event) => setIncludeExtendedPeriod(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              <span>Toon maand/ruimere periode</span>
            </label>
            <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={onlyExceptions}
                onChange={(event) => setOnlyExceptions(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              <span>Alleen uitzonderingen</span>
            </label>
            <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
              <input
                type="checkbox"
                checked={onlyWithoutMandagstaat}
                onChange={(event) => setOnlyWithoutMandagstaat(event.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              <span>Alleen uren zonder mandagstaat</span>
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={clearFilters}
            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
          >
            Filters wissen
          </button>
          <p className="text-xs text-slate-500">
            Selectie: week {data?.meta?.weekNumber || weekNumber} / {data?.meta?.year || year}
          </p>
        </div>

        {error ? <p className="text-sm text-rose-600">{error}</p> : null}
      </section>

      <section className="rounded-2xl bg-white p-4 shadow space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-full px-3 py-1.5 text-sm ${
                activeTab === tab.key
                  ? "bg-slate-900 text-white"
                  : "border border-slate-200 bg-slate-50 text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? <p className="text-sm text-slate-500">Laden...</p> : renderTabContent()}
      </section>

      <section className="rounded-2xl bg-white p-4 shadow space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Urenregels</h2>
          <p className="text-sm text-slate-500">
            Read-only controleweergave van urenregels, tariefstatus, mandagstaatstatus en financiële locks.
          </p>
        </div>

        {loading ? <p className="text-sm text-slate-500">Laden...</p> : null}

        {!loading && (!Array.isArray(data?.entries) || data.entries.length === 0) ? (
          <EmptyState text="Geen urenregels gevonden voor deze selectie." />
        ) : null}

        {!loading && Array.isArray(data?.entries) && data.entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Datum</th>
                  <th className="py-2 pr-3">Medewerker</th>
                  <th className="py-2 pr-3">Personeelsnr</th>
                  <th className="py-2 pr-3">Klant</th>
                  <th className="py-2 pr-3">Project</th>
                  <th className="py-2 pr-3">Werkbon</th>
                  <th className="py-2 pr-3">Uursoort</th>
                  <th className="py-2 pr-3">Uren</th>
                  <th className="py-2 pr-3">Tariefstatus</th>
                  <th className="py-2 pr-3">Mandagstaatstatus</th>
                  <th className="py-2 pr-3">Lockstatus</th>
                  <th className="py-2 pr-3">Bron</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry._id} className="border-b align-top">
                    <td className="py-2 pr-3">{entry.dateLabel}</td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{entry.employeeName}</div>
                      <div className="text-xs text-slate-500">{entry.approvalStatusLabel}</div>
                    </td>
                    <td className="py-2 pr-3">{entry.personnelNumber || "-"}</td>
                    <td className="py-2 pr-3">{entry.clientName || "-"}</td>
                    <td className="py-2 pr-3">{entry.projectName || "-"}</td>
                    <td className="py-2 pr-3">{entry.workorderNumber || "-"}</td>
                    <td className="py-2 pr-3">{entry.hourTypeName || "-"}</td>
                    <td className="py-2 pr-3">{formatHours(entry.hours)}</td>
                    <td className="py-2 pr-3">
                      <InfoBadge label={entry.tariffStatusLabel} tone={entry.tariffStatus} />
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-2">
                        <InfoBadge label={entry.mandagstaatStatusLabel} tone={entry.mandagstaatStatus} />
                        {entry.mandagstaatReference ? (
                          <span className="text-xs text-slate-500">{entry.mandagstaatReference}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-2">
                        <InfoBadge label={entry.lockStatusLabel} tone={entry.lockStatus} />
                        {entry.lockReasonLabel && entry.lockStatus === "locked" ? (
                          <span className="text-xs text-slate-500">{entry.lockReasonLabel}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <div>{entry.sourceLabel}</div>
                      {entry.isException ? (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {entry.exceptionLabels.map((label) => (
                            <InfoBadge key={`${entry._id}:${label}`} label={label} tone="missing" />
                          ))}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
